import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ticketsApi } from '../../api/tickets';
import { Send, Clock, Paperclip, ClipboardCheck, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import PageHeader from '../../components/PageHeader';
import EmptyState from '../../components/EmptyState';
import MessageContent from '../../components/MessageContent';
import { MessageCollapseToggle, MessagePreviewLine, ExpandCollapseAll } from '../../components/MessageCollapse';
import { messageId, messagePreview, isCollapsible, newestFirst } from '../../lib/messageThread';
import { useMessageCollapse } from '../../hooks/useMessageCollapse';
import TaskList from '../../components/tickets/TaskList';
import { useAuth } from '../../contexts/AuthContext';
import { useCompany } from '../../contexts/CompanyContext';
import { unreferencedAttachments, collectGalleryImages, isImageAttachment } from '../../lib/inlineImages';
import { ImageLightboxProvider, useImageLightbox } from '../../components/ImageLightbox';
import { StatusBadge, PriorityBadge } from '../../components/badges';

/** Attachment chip: images open in the in-app lightbox pop-up, everything
 *  else opens/downloads in a new tab as before. */
function AttachmentLink({ att, className, iconClass }: { att: any; className: string; iconClass: string }) {
  const lightbox = useImageLightbox();
  if (lightbox && att.url && isImageAttachment(att)) {
    return (
      <button
        type="button"
        onClick={() => lightbox.openImage(att.url, att.filename)}
        className={className}
        style={{ cursor: 'pointer' }}
        title={`View ${att.filename}`}
      >
        <Paperclip className={iconClass} />
        {att.filename}
      </button>
    );
  }
  return (
    <a href={att.url} target="_blank" rel="noopener noreferrer" className={className}>
      <Paperclip className={iconClass} />
      {att.filename}
    </a>
  );
}

export default function PortalTicketDetail() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { currentCompany } = useCompany();
  const myId: string | null = (user as any)?.id ?? (user as any)?._id ?? null;
  const companyId: string | undefined = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid;
  const [replyContent, setReplyContent] = useState('');

  const { data: ticket, isLoading } = useQuery({
    queryKey: ['ticket', id],
    queryFn: () => ticketsApi.get(id!),
    enabled: !!id,
  });

  const { data: messages } = useQuery({
    queryKey: ['ticket-messages', id],
    queryFn: () => ticketsApi.getMessages(id!),
    enabled: !!id,
  });

  const replyMutation = useMutation({
    mutationFn: (content: string) => ticketsApi.createMessage(id!, { content, type: 'reply' }),
    onSuccess: () => {
      setReplyContent('');
      queryClient.invalidateQueries({ queryKey: ['ticket-messages', id] });
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      toast.success('Reply sent');
    },
    onError: (err: any) => toast.error(err.message || 'Failed to send reply'),
  });

  const handleReply = (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyContent.trim()) return;
    replyMutation.mutate(replyContent.trim());
  };

  // Newest message first, older ones folded to a one-line preview — same
  // as the staff view (cTKT-00295).
  const thread = useMemo(() => newestFirst((messages ?? []) as any[]), [messages]);
  const collapse = useMessageCollapse(thread.map(messageId), id);

  // Every image the page displays, in display order — the lightbox pop-up's
  // arrows walk this set.
  const galleryImages = useMemo(() => collectGalleryImages([
    { content: String(ticket?.description ?? ''), attachments: ((ticket?.attachments ?? []) as any[]) },
    ...thread.map((m: any) => ({ content: String(m.content ?? ''), attachments: m.attachments ?? [] })),
  ]), [ticket, thread]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 pb-10">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-row-odd rounded w-48" />
          <div className="h-40 bg-row-odd rounded" />
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="flex flex-col gap-4 pb-10">
        <div className="card">
          <EmptyState>Ticket not found.</EmptyState>
        </div>
      </div>
    );
  }

  const isClosed = ['resolved', 'closed'].includes((ticket as any).statusCategory ?? ticket.status);

  return (
    <ImageLightboxProvider images={galleryImages}>
    <div className="flex flex-col gap-4 pb-10">
      <PageHeader
        title={ticket.title}
        intro={
          <span className="flex items-center gap-4 flex-wrap">
            <span className="font-mono font-bold text-primary">#{ticket.ticketNumber}</span>
            <span className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              Created {new Date(ticket.createdAt).toLocaleDateString()}
            </span>
            <span className="flex items-center gap-1">
              Priority: <PriorityBadge priority={ticket.priority} />
            </span>
            {ticket.category && <span>Category: {ticket.category.name}</span>}
          </span>
        }
        actions={<StatusBadge status={ticket.status} />}
      />

      {/* Description */}
      <div className="card">
        <h2 className="section-title">Description</h2>
        <MessageContent content={String(ticket.description ?? '')} />
        {unreferencedAttachments(String(ticket.description ?? ''), (ticket.attachments ?? []) as any[]).length > 0 && (
          <div className="mt-4 pt-4 border-t border-line">
            <p className="text-xs font-medium text-muted mb-2">Attachments</p>
            <div className="flex flex-wrap gap-2">
              {unreferencedAttachments(String(ticket.description ?? ''), (ticket.attachments ?? []) as any[]).map((a: any) => (
                <AttachmentLink
                  key={a.id}
                  att={a}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-row-odd text-sm text-primary hover:bg-row-even transition-colors"
                  iconClass="w-3.5 h-3.5"
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Sub-ticket tasks: shown when there are any, or when you're a
          collaborator who can add them. */}
      {(((ticket as any).tasks?.length ?? 0) > 0 || (ticket as any).isCollaborator) && (
        <TaskList
          ticketId={id!}
          companyId={companyId}
          tasks={(ticket as any).tasks ?? []}
          summary={(ticket as any).taskSummary}
          myId={myId}
          isStaff={false}
          isCollaborator={!!(ticket as any).isCollaborator}
          linkBase="/portal/tickets"
          emailIncludeContent={!!(ticket as any).emailIncludeContent}
        />
      )}

      {/* Collaborators (read-only here) */}
      {((ticket as any).collaborators?.length ?? 0) > 0 && (
        <div className="card">
          <h2 className="section-title flex items-center gap-2"><Users className="w-4 h-4 text-muted" /> Collaborators</h2>
          <p className="text-sm text-fg">
            {((ticket as any).collaborators as any[]).map(c => c.user?.name || c.user?.email).filter(Boolean).join(', ')}
          </p>
          <p className="text-xs text-muted mt-1">Collaborators can see this ticket and work its tasks.</p>
        </div>
      )}

      {/* Reply form — above the thread, next to the newest message. */}
      {!isClosed && (
        <form onSubmit={handleReply} className="card">
          <textarea
            value={replyContent}
            onChange={(e) => setReplyContent(e.target.value)}
            placeholder="Write a reply..."
            rows={4}
            className="input resize-y"
          />
          <div className="flex justify-end mt-3">
            <button
              type="submit"
              disabled={!replyContent.trim() || replyMutation.isPending}
              className="btn btn-primary flex items-center gap-2 disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
              {replyMutation.isPending ? 'Sending...' : 'Send Reply'}
            </button>
          </div>
        </form>
      )}

      {isClosed && (
        <div className="card text-center text-sm text-muted">
          This ticket is {ticket.status}. {ticket.resolution && (
            <span>Resolution: {ticket.resolution}</span>
          )}
        </div>
      )}

      {/* Messages — newest first */}
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="section-title">Conversation</h2>
          {thread.some(isCollapsible) && (
            <ExpandCollapseAll onExpand={collapse.expandAll} onCollapse={collapse.collapseAll} />
          )}
        </div>
        {thread.length > 0 ? (
          thread.map((msg: any) => {
            const content = String(msg.content ?? '');
            // Skip attachments already shown inline in the content.
            const chipAtts = unreferencedAttachments(content, (msg.attachments ?? []) as any[]);
            const mid = messageId(msg);
            const toggle = isCollapsible(msg) ? () => collapse.toggle(mid) : undefined;
            const collapsed = !!toggle && !collapse.isExpanded(mid);
            const authorName = msg.authorName || msg.author?.name || 'Unknown';
            return (
            <div
              key={mid}
              className={`card ${collapsed ? 'cursor-pointer' : ''}`}
              onClick={collapsed ? toggle : undefined}
              style={{
                borderLeft: '4px solid',
                borderLeftColor:
                  msg.author?.id === (ticket.createdBy as any)?.id
                    ? 'var(--scpa-primary)'
                    : 'var(--scpa-border)',
              }}
            >
              <div className={`flex items-center justify-between gap-2 flex-wrap ${collapsed ? 'mb-1' : 'mb-2'}`}>
                <span className="text-sm font-medium text-fg inline-flex items-center gap-2 flex-wrap">
                  {toggle && (
                    <MessageCollapseToggle expanded={!collapsed} onToggle={toggle} label={authorName} />
                  )}
                  {authorName}
                  {msg.type === 'task_note' && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-primary" title={msg.task?.title}>
                      <ClipboardCheck className="w-3 h-3" /> Task note{msg.task?.title ? ` · ${msg.task.title}` : ''}
                    </span>
                  )}
                </span>
                <span className="text-xs text-muted">
                  {new Date(msg.createdAt).toLocaleString()}
                </span>
              </div>
              {collapsed ? (
                <MessagePreviewLine text={messagePreview(content).text} attachmentCount={(msg.attachments ?? []).length} />
              ) : (
              <>
              <MessageContent content={content} />
              {chipAtts.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {chipAtts.map((a: any) => (
                    <AttachmentLink
                      key={a.id}
                      att={a}
                      className="flex items-center gap-1 text-xs text-primary hover:underline"
                      iconClass="w-3 h-3"
                    />
                  ))}
                </div>
              )}
              </>
              )}
            </div>
            );
          })
        ) : (
          <p className="text-sm text-muted">No replies yet</p>
        )}
      </div>
    </div>
    </ImageLightboxProvider>
  );
}
